import React, { useState } from 'react'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Eye,
  Heart,
  Share2,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  ChevronDown
} from 'lucide-react'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { formatCurrency, formatNumber } from '../../data/mockData'

// Platform performance data
const platformPerformance = [
  { month: 'Jul', gmv: 180000, campaigns: 12, creators: 45 },
  { month: 'Aug', gmv: 220000, campaigns: 15, creators: 52 },
  { month: 'Sep', gmv: 280000, campaigns: 18, creators: 61 },
  { month: 'Oct', gmv: 350000, campaigns: 22, creators: 75 },
  { month: 'Nov', gmv: 420000, campaigns: 28, creators: 89 },
  { month: 'Dec', gmv: 480000, campaigns: 32, creators: 102 },
  { month: 'Jan', gmv: 520000, campaigns: 35, creators: 115 }
]

// Campaign category breakdown
const categoryData = [
  { name: 'Medical Devices', value: 35, color: '#0066CC' },
  { name: 'Pharmaceuticals', value: 25, color: '#00B4A2' },
  { name: 'Digital Health', value: 20, color: '#7C3AED' },
  { name: 'Wellness', value: 12, color: '#F59E0B' },
  { name: 'Other', value: 8, color: '#6B7280' }
]

// Creator tier distribution
const tierData = [
  { name: 'Flagship', value: 15, color: '#7C3AED' },
  { name: 'Macro', value: 25, color: '#0066CC' },
  { name: 'Mid-Tier', value: 35, color: '#00B4A2' },
  { name: 'Micro', value: 25, color: '#6B7280' }
]

// Engagement by platform
const platformEngagement = [
  { platform: 'Instagram', reach: 2400000, engagement: 4.8, posts: 156 },
  { platform: 'LinkedIn', reach: 1800000, engagement: 5.2, posts: 89 },
  { platform: 'YouTube', reach: 3200000, engagement: 6.1, posts: 45 },
  { platform: 'TikTok', reach: 1200000, engagement: 7.8, posts: 78 },
  { platform: 'Twitter', reach: 890000, engagement: 3.2, posts: 234 }
]

// Top performing campaigns
const topCampaigns = [
  { name: 'Heart Health Awareness', brand: 'CardioTech', roi: 425, gmv: 125000 },
  { name: 'Mental Wellness Month', brand: 'MindCare', roi: 380, gmv: 98000 },
  { name: 'Diabetes Innovation', brand: 'GlucoSense', roi: 352, gmv: 87500 },
  { name: 'Sleep Science', brand: 'RestWell', roi: 298, gmv: 72000 },
  { name: 'Fitness Tech Launch', brand: 'FitPro', roi: 265, gmv: 65000 }
]

function AdminAnalytics() {
  const [dateRange, setDateRange] = useState('6m')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Analytics</h1>
          <p className="text-gray-500">Comprehensive insights into marketplace performance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input appearance-none pr-10"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="3m">Last 3 months</option>
              <option value="6m">Last 6 months</option>
              <option value="1y">Last year</option>
            </select>
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">Total GMV</p>
            <span className="flex items-center text-green-600 text-sm">
              <TrendingUp className="w-4 h-4 mr-1" />
              +24%
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">$2.45M</p>
          <p className="text-sm text-gray-400 mt-1">vs $1.98M last period</p>
        </div>
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">Active Campaigns</p>
            <span className="flex items-center text-green-600 text-sm">
              <TrendingUp className="w-4 h-4 mr-1" />
              +18%
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">47</p>
          <p className="text-sm text-gray-400 mt-1">12 launched this month</p>
        </div>
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">Verified Creators</p>
            <span className="flex items-center text-green-600 text-sm">
              <TrendingUp className="w-4 h-4 mr-1" />
              +32%
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">248</p>
          <p className="text-sm text-gray-400 mt-1">28 pending verification</p>
        </div>
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-500">Avg. Campaign ROI</p>
            <span className="flex items-center text-green-600 text-sm">
              <TrendingUp className="w-4 h-4 mr-1" />
              +15%
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">342%</p>
          <p className="text-sm text-gray-400 mt-1">Industry avg: 180%</p>
        </div>
      </div>

      {/* GMV & Growth Chart */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Platform Growth</h2>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-hf-teal-600 rounded-full" />
              <span className="text-gray-500">GMV ($)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-healthcare-teal rounded-full" />
              <span className="text-gray-500">Campaigns</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full" />
              <span className="text-gray-500">Creators</span>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={platformPerformance}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
            <YAxis yAxisId="left" stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
            <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" fontSize={12} />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
              formatter={(value, name) => {
                if (name === 'gmv') return [`$${formatNumber(value)}`, 'GMV']
                return [value, name.charAt(0).toUpperCase() + name.slice(1)]
              }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="gmv"
              stroke="#0066CC"
              fill="#0066CC"
              fillOpacity={0.1}
              strokeWidth={2}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="campaigns"
              stroke="#00B4A2"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="creators"
              stroke="#7C3AED"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Category & Tier Distribution */}
      <div className="grid grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Campaign Categories</h2>
          <div className="flex items-center">
            <ResponsiveContainer width="50%" height={200}>
              <RechartsPie>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </RechartsPie>
            </ResponsiveContainer>
            <div className="space-y-2">
              {categoryData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">{item.name}</span>
                  <span className="text-sm font-medium text-gray-900">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Creator Tiers</h2>
          <div className="flex items-center">
            <ResponsiveContainer width="50%" height={200}>
              <RechartsPie>
                <Pie
                  data={tierData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                >
                  {tierData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </RechartsPie>
            </ResponsiveContainer>
            <div className="space-y-2">
              {tierData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">{item.name}</span>
                  <span className="text-sm font-medium text-gray-900">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Engagement */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Engagement by Platform</h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={platformEngagement} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis type="number" stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `${v/1000000}M`} />
            <YAxis type="category" dataKey="platform" stroke="#9CA3AF" fontSize={12} width={80} />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
              formatter={(value, name) => {
                if (name === 'reach') return [formatNumber(value), 'Total Reach']
                return [value, name.charAt(0).toUpperCase() + name.slice(1)]
              }}
            />
            <Bar dataKey="reach" fill="#0066CC" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-5 gap-4 mt-4 pt-4 border-t">
          {platformEngagement.map((platform) => (
            <div key={platform.platform} className="text-center">
              <p className="text-sm text-gray-500">{platform.platform}</p>
              <p className="text-lg font-semibold text-gray-900">{platform.engagement}%</p>
              <p className="text-xs text-gray-400">Avg. Engagement</p>
            </div>
          ))}
        </div>
      </div>

      {/* Top Campaigns */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Top Performing Campaigns</h2>
          <button className="text-hf-teal-600 text-sm font-medium hover:text-hf-teal-700">
            View All
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm text-gray-500 border-b">
              <th className="pb-3 font-medium">Campaign</th>
              <th className="pb-3 font-medium">Brand</th>
              <th className="pb-3 font-medium text-right">GMV</th>
              <th className="pb-3 font-medium text-right">ROI</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {topCampaigns.map((campaign, index) => (
              <tr key={index} className="text-sm">
                <td className="py-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="font-medium text-gray-900">{campaign.name}</span>
                  </div>
                </td>
                <td className="py-3 text-gray-500">{campaign.brand}</td>
                <td className="py-3 text-right font-medium text-gray-900">
                  {formatCurrency(campaign.gmv)}
                </td>
                <td className="py-3 text-right">
                  <span className="inline-flex items-center text-green-600 font-medium">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    {campaign.roi}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Eye className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">12.4M</p>
          <p className="text-sm text-gray-500">Total Impressions</p>
          <p className="text-xs text-green-600 mt-1">+18% from last month</p>
        </div>
        <div className="card text-center">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Heart className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">648K</p>
          <p className="text-sm text-gray-500">Total Engagements</p>
          <p className="text-xs text-green-600 mt-1">+25% from last month</p>
        </div>
        <div className="card text-center">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Share2 className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">42.8K</p>
          <p className="text-sm text-gray-500">Total Shares</p>
          <p className="text-xs text-green-600 mt-1">+32% from last month</p>
        </div>
        <div className="card text-center">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Target className="w-6 h-6 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">5.2%</p>
          <p className="text-sm text-gray-500">Avg. Engagement Rate</p>
          <p className="text-xs text-green-600 mt-1">Above industry avg (3.8%)</p>
        </div>
      </div>
    </div>
  )
}

export default AdminAnalytics
